// src/components/MuscleMap.js
import { useEffect, useRef } from 'react';
import './MuscleMap.css';
import '../pages/WorkoutPage.css';


// FRONT
import { ReactComponent as MusclesFront }    from '../assets/muscles/muscles_front_layer_1.drawio.svg';
import { ReactComponent as SilhouetteFront } from '../assets/muscles/muscles_front_layer_2.drawio.svg';
// BACK
import { ReactComponent as MusclesBack }     from '../assets/muscles/muscles_back_layer_1.drawio.svg';
import { ReactComponent as SilhouetteBack }  from '../assets/muscles/muscles_back_layer_2.drawio.svg';

const IGNORE_FLAGS = new Set(['primary','secondary','planned']);

const decodeEntities = (s) => { const el = document.createElement('textarea'); el.innerHTML = s; return el.value; };

// "chest__pec_major" -> "pec_major"; "front quads" -> "front_quads"
function slugFromIdOrText(s) {
  if (!s) return null;
  let v = String(s).toLowerCase().trim();
  if (v.includes('__')) v = v.split('__').pop(); // drop group prefix
  v = v.replace(/[^\w\s-]+/g, ' ')
       .replace(/\s+/g, '_')
       .replace(/-+/g, '_');
  return v;
}

// classes like "front quads" -> "front_quads"; keeps front/back/side
function slugFromClassList(cls) {
  const tokens = String(cls).toLowerCase().trim().split(/\s+/).filter(Boolean);
  const parts = tokens.filter(t => !IGNORE_FLAGS.has(t));
  return parts.length ? parts.join('_') : null;
}

// Attach .muscle & data-muscle-slug to visible nodes (draw.io export)
function augmentSvgFromMxContent(svgEl) {
  if (!svgEl || svgEl.dataset.augmented) return;

  const raw = svgEl.getAttribute('content');
  if (!raw) { svgEl.dataset.augmented = '1'; return; }

  let xml; try { xml = new DOMParser().parseFromString(decodeEntities(raw), 'text/xml'); }
  catch { svgEl.dataset.augmented = '1'; return; }

  const objects = Array.from(xml.querySelectorAll('object[class]'));
  for (const obj of objects) {
    const id  = obj.getAttribute('id');
    const cls = obj.getAttribute('class') || '';
    const slug = slugFromClassList(cls);
    if (!id || !slug) continue;

    const host = svgEl.querySelector(`[data-cell-id="${id}"]`);
    if (!host) continue;

    host.classList.add('muscle');
    host.dataset.muscleSlug  = slug;          // e.g., "front_quads", "pec_major"
    host.dataset.muscleLabel = cls.trim();
    host.setAttribute('pointer-events','bounding-box');

    // Remove inline paints so CSS controls color
    host.querySelectorAll('*').forEach(n => {
      n.removeAttribute('fill'); n.removeAttribute('stroke'); n.removeAttribute('style');
    });
  }

  svgEl.dataset.augmented = '1';
}

export default function MuscleMap({
  primary = new Set(),   // can be "front quads" | "front_quads" | "legs__front_quads"
  secondary = new Set(),
  exercises = [],
  mode = 'track',
  onMuscleClick,
  height // "220px" | "26vh" | "clamp(160px,26vh,280px)"
}) {
  const fgFrontRef = useRef(null);
  const fgBackRef  = useRef(null);

  const styleVar = height ? { '--map-height': typeof height === 'number' ? `${height}px` : String(height) } : undefined;

  useEffect(() => {
    const svgs = [
      fgFrontRef.current?.querySelector('svg'),
      fgBackRef.current?.querySelector('svg'),
    ].filter(Boolean);

    // 1) augment both front & back
    svgs.forEach(augmentSvgFromMxContent);

    // 2) normalize inputs once
    const primarySlugs   = new Set([...primary].map(slugFromIdOrText).filter(Boolean));
    const secondarySlugs = new Set([...secondary].map(slugFromIdOrText).filter(Boolean));
    const plannedSlugs   = new Set(exercises.map(e => slugFromIdOrText(e?.muscleGroupId)).filter(Boolean));

    // 3) apply highlights to both SVGs
    svgs.forEach(svg => {
      svg.querySelectorAll('[data-muscle-slug]').forEach(el => {
        el.classList.remove('primary','secondary','planned');
        const slug = el.dataset.muscleSlug;
        if (!slug) return;
        if (primarySlugs.has(slug))       el.classList.add('primary');
        else if (secondarySlugs.has(slug))el.classList.add('secondary');
        else if (plannedSlugs.has(slug))  el.classList.add('planned');
      });
    });
  }, [primary, secondary, exercises, mode]);

  const handleClick = (e) => {
    const n = e.target.closest('[data-muscle-slug], .muscle');
    if (!n) return;
    const slug = n.dataset?.muscleSlug || slugFromIdOrText([...n.classList].join(' '));
    if (slug) onMuscleClick?.(slug);
  };

  return (
    <div className={`panel panel--stretch map-panel ${mode === 'plan' ? 'plan-glow' : 'track-glow'}`}>
      
      {/* bigger, smoother glows */}
      <svg width="0" height="0" aria-hidden="true" style={{ position: 'absolute' }}>
        <defs>
          {/* GREEN glow (track) */}
          <filter id="glow-green"
                  x="-60%" y="-60%" width="220%" height="220%"
                  colorInterpolationFilters="sRGB">
            <feMorphology in="SourceAlpha" operator="dilate" radius="3" result="spread"/>
            <feGaussianBlur in="spread" stdDeviation="18" result="blur"/>
            <feFlood floodColor="#00ff64" floodOpacity="0.70" result="flood"/>
            <feComposite in="flood" in2="blur" operator="in" result="glow"/>
            <feMerge>
              <feMergeNode in="glow"/>
              <feMergeNode in="glow"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>

          {/* ORANGE glow (plan) */}
          <filter id="glow-orange"
                  x="-60%" y="-60%" width="220%" height="220%"
                  colorInterpolationFilters="sRGB">
            <feMorphology in="SourceAlpha" operator="dilate" radius="3" result="spread"/>
            <feGaussianBlur in="spread" stdDeviation="18" result="blur"/>
            <feFlood floodColor="#ffa500" floodOpacity="0.75" result="flood"/>
            <feComposite in="flood" in2="blur" operator="in" result="glow"/>
            <feMerge>
              <feMergeNode in="glow"/>
              <feMergeNode in="glow"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
      </svg>
      
      <div className="map-viewport" style={styleVar}>
        <div className="map-grid">
          {/* FRONT */}
          <div className="map-stack map-col map-col--front">
            <SilhouetteFront className="map-bg" aria-hidden="true" preserveAspectRatio="xMidYMid meet" />
            <div className="map-fg" ref={fgFrontRef} onClick={handleClick}>
              <MusclesFront aria-label="Muscle map front" role="img" preserveAspectRatio="xMidYMid meet" />
            </div>
          </div>

          {/* BACK */}
          <div className="map-stack map-col map-col--back">
            <SilhouetteBack className="map-bg" aria-hidden="true" preserveAspectRatio="xMidYMid meet" />
            <div className="map-fg" ref={fgBackRef} onClick={handleClick}>
              <MusclesBack aria-label="Muscle map back" role="img" preserveAspectRatio="xMidYMid meet" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}